import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import WeatherWidget from '../components/WeatherWidget'
import TaskList from '../components/TaskList'
import { OnboardingWizard } from '../components/OnboardingWizard'
import { CuratedCollections } from '../components/CuratedCollections'

interface DashboardProps {
  user: {
    id: string
    email: string
    zipCode?: string
    frostDate?: Date
    isPremium: boolean
  }
}

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const navigate = useNavigate()
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [onboardingData, setOnboardingData] = useState<any>(null)

  useEffect(() => {
    const saved = localStorage.getItem('gardenPlannerOnboarding')
    if (saved) {
      try {
        setOnboardingData(JSON.parse(saved))
      } catch (e) {
        console.error('Failed to parse onboarding data')
      }
    }
  }, [])

  const handleOnboardingComplete = (data: any) => {
    localStorage.setItem('gardenPlannerOnboarding', JSON.stringify(data))
    setOnboardingData(data)
    setShowOnboarding(false)
  }

  const handleSelectCollection = (plantIds: string[]) => {
    // Save selected collection plants to localStorage for use in designer
    localStorage.setItem('gardenPlannerSelectedCollection', JSON.stringify(plantIds))
    // Navigate to designer using React Router (preserves auth state)
    navigate('/designer')
  }

  return (
    <div>
      <div className="card">
        <h2>Welcome back! 👋</h2>
        <p>Here's what's happening in your garden today.</p>
        {user.zipCode && (
          <p style={{ color: '#666', marginTop: '0.5rem' }}>
            📍 Location: {user.zipCode} | 
            {user.frostDate && (
              <> Last Frost: {user.frostDate.toLocaleDateString()}</>
            )}
          </p>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Left Column - Weather */}
        <div>
          <WeatherWidget zipCode={user.zipCode} />
        </div>

        {/* Right Column - Tasks */}
        <div>
          <TaskList zipCode={user.zipCode} frostDate={user.frostDate} />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h3>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <Link to="/designer" className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-block', padding: '0.75rem 1.5rem', background: '#2d5a27', color: 'white', borderRadius: '6px' }}>
            🎨 Design Garden
          </Link>
          <Link to="/plants" className="btn btn-secondary" style={{ textDecoration: 'none', display: 'inline-block', padding: '0.75rem 1.5rem', background: '#f5f5f0', color: '#2d5a27', border: '1px solid #ddd', borderRadius: '6px' }}>
            🌿 Browse Plants
          </Link>
          <Link to="/calendar" className="btn btn-secondary" style={{ textDecoration: 'none', display: 'inline-block', padding: '0.75rem 1.5rem', background: '#f5f5f0', color: '#2d5a27', border: '1px solid #ddd', borderRadius: '6px' }}>
            📅 View Calendar
          </Link>
          <button 
            onClick={() => setShowOnboarding(true)}
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem', 
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', 
              color: 'white', 
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            🎯 Update Preferences
          </button>
        </div>

        {showOnboarding && (
          <OnboardingWizard
            onComplete={handleOnboardingComplete}
            onSkip={() => setShowOnboarding(false)}
          />
        )}
      </div>

      {/* Curated Collections */}
      {onboardingData && (
        <div className="card" style={{ marginTop: '1.5rem', padding: 0, overflow: 'hidden' }}>
          <CuratedCollections
            experience={onboardingData.experience || 'intermediate'}
            interests={onboardingData.interests || ['vegetables']}
            growingMethod={onboardingData.growingMethod || 'raised'}
            space={onboardingData.space || 'medium'}
            onSelectCollection={handleSelectCollection}
          />
        </div>
      )}

      {/* Tips */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h3>💡 Today's Gardening Tip</h3>
        <p style={{ marginTop: '0.5rem', color: '#555' }}>
          {getDailyTip()}
        </p>
      </div>
    </div>
  )
}

const getDailyTip = () => {
  const tips = [
    "Water deeply and less frequently to encourage deep root growth.",
    "Mulch around plants to retain moisture and suppress weeds.",
    "Plant flowers near vegetables to attract pollinators.",
    "Rotate your crops each year to prevent soil-borne diseases.",
    "Start a compost pile to create free, nutrient-rich soil.",
    "Save seeds from your best plants for next year's garden.",
    "Check plants daily for pests and diseases.",
    "Harvest in the morning when plants are most hydrated.",
    "Keep a garden journal to track what works best.",
    "Use companion planting to naturally deter pests.",
    "Leave some plants to flower for beneficial insects.",
    "Prune tomato suckers for better fruit production.",
    "Add eggshells to soil for calcium-loving plants.",
    "Use coffee grounds around acid-loving plants.",
    "Plant a cover crop in fall to improve soil health."
  ]
  
  // Get tip based on day of year
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24))
  return tips[dayOfYear % tips.length]
}

export default Dashboard
